import { useSyncExternalStore } from "react";

export interface SalesOrderCellPopoverTarget {
  /** The clicked cell element — popover anchor and outline host. */
  anchorEl: HTMLElement;
  /**
   * The active segment dimensions and this row's value for each — e.g.
   * { channel: "Amazon", sku: "A00360" }. Whatever the Demand view is grouped
   * by; the transform filters sales_order_master by these same field→value
   * pairs. Unlike the PO popover's fixed (sku, location), this is dynamic.
   */
  segments: Record<string, string>;
  /** Epoch ms of the clicked date column (the pivot period start). */
  periodStartMs: number;
  /** The pivot's active date hierarchy — sets how rows bucket into the cell. */
  dateHierarchy: "day" | "week" | "month" | "quarter" | "year";
  /**
   * The pivot measure the clicked cell shows. The daily order table only
   * carries quantities, so the popover says so under any other measure.
   */
  measure?: string | null;
  /**
   * The view has Filter-by chips active. The transform narrows by segments
   * and period only, so the list may include orders the cell excluded.
   */
  hasViewFilters?: boolean;
}

// Module-level store so pivot cells can open the popover without threading
// props (or state) through MUIFlatTable / the pivot builder. Opening and
// closing re-renders ONLY subscribers (the popover itself) — the table's
// props never change, so react-virtuoso repaints zero cells. Mirrors the
// PO cell popover's isolation contract.
let currentTarget: SalesOrderCellPopoverTarget | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function openSalesOrderCellPopover(
  target: SalesOrderCellPopoverTarget,
): void {
  currentTarget = target;
  emit();
}

export function closeSalesOrderCellPopover(): void {
  if (currentTarget === null) return;
  currentTarget = null;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): SalesOrderCellPopoverTarget | null {
  return currentTarget;
}

export function useSalesOrderCellPopoverTarget(): SalesOrderCellPopoverTarget | null {
  return useSyncExternalStore(subscribe, getSnapshot);
}
